import { useCallback, useEffect, useState } from "react";

import { useSession } from "../../session/SessionContext.js";

/**
 * The knowledge corpus.
 *
 * The screen is built around one act: **approval**. Ingesting a document is
 * mechanical — chunk it, embed it, store it — while approving it decides what the
 * assistant may assert on Island Peptides' behalf, and for a regulated product that
 * is the most consequential thing available here.
 *
 * So content synced from WooCommerce arrives unapproved and answers nothing until
 * somebody reads it and says yes. The screen leads with what is waiting rather than
 * burying it in a list, because a corpus where nothing has been approved is a corpus
 * where the assistant declines every question, and the reason should be obvious.
 */

interface KnowledgeDocument {
  documentId: string;
  source: string;
  visibility: "public" | "internal";
  title: string;
  approved: boolean;
  url?: string;
  updatedAt: string;
}

type Filter = "pending" | "approved" | "all";

export function KnowledgePanel() {
  const { api, can } = useSession();

  const [documents, setDocuments] = useState<readonly KnowledgeDocument[]>([]);
  const [filter, setFilter] = useState<Filter>("pending");
  const [status, setStatus] = useState<"loading" | "ready" | "error">("loading");
  const [busy, setBusy] = useState<string | null>(null);
  const [message, setMessage] = useState<string | null>(null);

  const [draftTitle, setDraftTitle] = useState("");
  const [draftBody, setDraftBody] = useState("");
  const [draftInternal, setDraftInternal] = useState(false);

  const writable = can("admin:knowledge:write");

  const load = useCallback(async () => {
    try {
      const query = filter === "all" ? "" : `?approved=${filter === "approved" ? "true" : "false"}`;

      const body = await api.get<{ documents: KnowledgeDocument[] }>(
        `/admin/knowledge/documents${query}`,
      );

      setDocuments(body.documents);
      setStatus("ready");
    } catch {
      setStatus("error");
    }
  }, [api, filter]);

  useEffect(() => {
    void load();
  }, [load]);

  async function act(documentId: string, work: () => Promise<unknown>): Promise<void> {
    setBusy(documentId);
    setMessage(null);

    try {
      await work();
      await load();
    } catch {
      setMessage("That did not work. Please try again.");
    } finally {
      setBusy(null);
    }
  }

  async function addArticle(): Promise<void> {
    if (draftTitle.trim() === "" || draftBody.trim() === "") return;

    setBusy("new");
    setMessage(null);

    try {
      await api.post("/admin/knowledge/documents", {
        source: "article",
        visibility: draftInternal ? "internal" : "public",
        title: draftTitle.trim(),
        body: draftBody.trim(),
        // Written unapproved even here, so a new article is read once more before it
        // can be quoted to a customer.
        approved: false,
      });

      setDraftTitle("");
      setDraftBody("");
      setDraftInternal(false);
      setMessage("Added. It will not be used until you approve it.");
      await load();
    } catch {
      setMessage("That article could not be added.");
    } finally {
      setBusy(null);
    }
  }

  if (status === "loading") return <p className="muted">Loading…</p>;

  if (status === "error") {
    return <p className="error">The knowledge base could not be reached.</p>;
  }

  return (
    <div className="admin-form">
      <section className="card">
        <div className="row">
          <h2>Documents</h2>
          <span className="spacer" />

          {(["pending", "approved", "all"] as const).map((option) => (
            <button
              key={option}
              className={filter === option ? "primary" : ""}
              onClick={() => {
                setFilter(option);
              }}
            >
              {option === "pending"
                ? "Awaiting approval"
                : option === "approved"
                  ? "Approved"
                  : "All"}
            </button>
          ))}
        </div>

        {filter === "pending" && documents.length === 0 && (
          <p className="muted">Nothing is waiting for approval.</p>
        )}

        {filter !== "pending" && documents.length === 0 && (
          <p className="muted">
            Nothing here yet. Sync the shop&apos;s content or write an article below.
          </p>
        )}

        <table className="grid">
          <tbody>
            {documents.map((document) => (
              <tr key={document.documentId}>
                <td>
                  <strong>{document.title}</strong>
                  <div className="small muted">
                    {document.source}
                    {document.visibility === "internal" && " · internal — never shown to customers"}
                    {document.url !== undefined && (
                      <>
                        {" · "}
                        <a href={document.url} target="_blank" rel="noreferrer">
                          source
                        </a>
                      </>
                    )}
                  </div>
                </td>
                <td className="small">
                  <span className={document.approved ? "tag live" : "tag"}>
                    {document.approved ? "Answerable" : "Not used"}
                  </span>
                </td>
                <td className="right">
                  {writable && (
                    <>
                      <button
                        disabled={busy === document.documentId}
                        className={document.approved ? "" : "primary"}
                        onClick={() => {
                          void act(document.documentId, async () =>
                            api.post(`/admin/knowledge/documents/${document.documentId}/approval`, {
                              approved: !document.approved,
                            }),
                          );
                        }}
                      >
                        {document.approved ? "Withdraw" : "Approve"}
                      </button>
                      <button
                        className="danger"
                        disabled={busy === document.documentId}
                        onClick={() => {
                          void act(document.documentId, async () =>
                            api.delete(`/admin/knowledge/documents/${document.documentId}`),
                          );
                        }}
                      >
                        Delete
                      </button>
                    </>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>

        {message !== null && <p className="small muted">{message}</p>}
      </section>

      {writable && (
        <>
          <section className="card">
            <h2>Write an article</h2>
            <p className="hint">
              For anything the shop does not publish: approved promotions, internal notes for
              agents, answers to questions that keep coming up.
            </p>

            <label>
              Title
              <input
                type="text"
                value={draftTitle}
                maxLength={300}
                onChange={(event) => {
                  setDraftTitle(event.target.value);
                }}
              />
            </label>

            <label>
              Body
              <textarea
                rows={6}
                value={draftBody}
                onChange={(event) => {
                  setDraftBody(event.target.value);
                }}
              />
            </label>

            <label className="checkbox">
              <input
                type="checkbox"
                checked={draftInternal}
                onChange={(event) => {
                  setDraftInternal(event.target.checked);
                }}
              />
              <span>
                Internal only
                <span className="hint">
                  Agents can find it; the assistant will never repeat it to a customer. This is
                  where dosing references belong.
                </span>
              </span>
            </label>

            <button
              className="primary"
              disabled={busy === "new" || draftTitle.trim() === "" || draftBody.trim() === ""}
              onClick={() => void addArticle()}
            >
              {busy === "new" ? "Adding…" : "Add article"}
            </button>
          </section>

          <section className="card">
            <h2>Sync the shop</h2>
            <p className="hint">
              Pulls published products, categories, posts and policy pages from WooCommerce.
              Everything arrives <strong>unapproved</strong>: product copy is written to sell, and
              that is not the same as something a support representative should assert. Runs in the
              background and can take several minutes.
            </p>

            <button
              disabled={busy === "sync"}
              onClick={() => {
                void act("sync", async () => api.post("/admin/knowledge/sync", {}));
              }}
            >
              {busy === "sync" ? "Starting…" : "Start sync"}
            </button>
          </section>
        </>
      )}
    </div>
  );
}
